// TodoDemo.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  IconButton,
  Badge,
  useToast,
  Tooltip,
  Skeleton,
  SkeletonText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Divider,
  Flex,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import format from "date-fns/format";
import {
  deleteData,
  getData,
  getDataById,
  updateData,
} from "../../../redux/actions/common.action";
import { useNavigate } from "react-router-dom";
import { isValid } from "date-fns/esm";
import UpdateTodoWithComment from "./UpdateTodoWithComment";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";
import {
  CheckSquare,
  Clock,
  User,
  Calendar,
  Trash2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { normalizeId } from "../../../utils/teamUserForm";

interface Todo {
  _id: string;
  note: string;
  status: "Pending" | "Completed";
  parentId: string;
  assignId: string | { _id?: string; Name?: string; Email?: string; UserName?: string };
  assigneeName?: string;
  assigneeEmail?: string;
  createdBy: string | { _id?: string; Name?: string; Email?: string };
  createrName?: string;
  creatorEmail?: string;
  Branch: string;
  dueDate: Date;
  completedComment: string;
  createdAt: Date;
  updatedAt: Date;
  timeStatus: string;
}

function getTodoAssigneeId(todo: Todo | any): string {
  const a = todo?.assignId;
  if (a && typeof a === "object" && a !== null && "_id" in a) {
    return normalizeId(a._id);
  }
  return normalizeId(a);
}

function getTodoAssigneeDisplay(
  todo: Todo | any,
  usersList: any[] | undefined,
): { name: string; email: string } | null {
  if (todo?.assigneeName) {
    return {
      name: todo.assigneeName,
      email: todo.assigneeEmail || "",
    };
  }
  const a = todo?.assignId;
  if (a && typeof a === "object" && a !== null) {
    const name = a.Name;
    if (name) {
      return {
        name,
        email: a.Email || a.UserName || "",
      };
    }
  }
  const aid = getTodoAssigneeId(todo);
  if (!aid) return null;
  const u = usersList?.find((x: any) => normalizeId(x._id) === aid);
  if (u) {
    return {
      name: u.Name || "—",
      email: u.Email || "",
    };
  }
  return null;
}

/** ObjectId or populated { _id } from API */
function getTodoRefId(ref: unknown): string {
  if (ref && typeof ref === "object" && ref !== null && "_id" in ref) {
    return normalizeId((ref as { _id: unknown })._id);
  }
  return normalizeId(ref);
}

interface Todos {
  todos: Todo[];
}

interface TodoItem {
  todos: Todo[];
  users: any;
  signinuser: any;
  user: any;
  loading: boolean;
}

const TodoDemo = () => {
  const { users, signinuser, user, loading } = useSelector(
    (state: { common: TodoItem }) => state.common
  );
  const { todos } = useSelector((state: { common: Todos }) => state.common);

  const navigate = useNavigate();
  const toast = useToast();
  const dispatch: any = useDispatch();
  const endpoint = "todo";

  const [todoShow, setTodoShow] = useState<Todo[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [todoToDelete, setTodoToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleTodos = (status: string) => {
    if (status === "Total") {
      setTodoShow(todos);
    } else if (status === "Completed") {
      const filteredT = todos?.filter((todo: Todo) => todo?.status === status);
      setTodoShow(filteredT);
    } else if (status === "Pending") {
      const filteredT = todos?.filter((todo: Todo) => todo?.status === status);
      setTodoShow(filteredT);
    }
  };

  useEffect(() => {
    if (todos?.length > 0) {
      setTodoShow(todos);
    }
  }, [todos]);

  useEffect(() => {
    if (!signinuser?.userId) return;
    dispatch(getDataById(signinuser?.userId, "user"));
  }, [signinuser?.userId]);

  const handleMarkComplete = (id: number, completedComment: string) => {
    dispatch(
      updateData(
        id,
        endpoint,
        { status: "Completed", completedComment: completedComment },
        navigate,
        toast
      )
    ).then(() => {
      dispatch(getData(endpoint));
    });
  };

  const handleDeleteClick = (id: string) => {
    setTodoToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (todoToDelete) {
      setIsDeleting(true);
      dispatch(deleteData(todoToDelete, endpoint, toast))
        .then(() => {
          dispatch(getData(endpoint));
          setDeleteModalOpen(false);
          setTodoToDelete(null);
        })
        .finally(() => {
          setIsDeleting(false);
        });
    }
  };

  const totalCount = todos?.length;
  const completedCount = todos?.filter(
    (t: any) => t?.status === "Completed"
  ).length;
  const pendingCount = todos?.filter(
    (t: any) => t?.status === "Pending"
  ).length;

  const getTodoToDeleteName = () => {
    if (!todoToDelete) return "";
    const todo = todos?.find((t: Todo) => t._id === todoToDelete);
    return todo?.note || "";
  };

  // Helper function to check if task is overdue
  const isOverdue = (dueDate: Date) => {
    return new Date(dueDate) < new Date();
  };

  // Helper function to check if task was completed within time
  const isCompletedWithinTime = (dueDate: Date, updatedAt: Date) => {
    return new Date(updatedAt) <= new Date(dueDate);
  };

  return (
    <Box>
      {/* Header with Filters */}
      <Flex align="center" gap={2} mb={4}>
        <Box bg="purple.50" p={2} borderRadius="lg">
          <CheckSquare size={20} color="#9333EA" />
        </Box>
        <Text fontSize="md" fontWeight="bold" color="gray.800">
          Task List
        </Text>
      </Flex>

      <HStack spacing={2} mb={4} flexWrap="wrap">
        <Badge
          colorScheme="blue"
          px={3}
          py={1}
          borderRadius="full"
          cursor="pointer"
          fontSize="xs"
          onClick={() => handleTodos("Total")}
          _hover={{ transform: "scale(1.05)" }}
          transition="all 0.2s"
        >
          Total: {totalCount}
        </Badge>
        <Badge
          colorScheme="green"
          px={3}
          py={1}
          borderRadius="full"
          cursor="pointer"
          fontSize="xs"
          onClick={() => handleTodos("Completed")}
          _hover={{ transform: "scale(1.05)" }}
          transition="all 0.2s"
        >
          Completed: {completedCount}
        </Badge>
        <Badge
          colorScheme="orange"
          px={3}
          py={1}
          borderRadius="full"
          cursor="pointer"
          fontSize="xs"
          onClick={() => handleTodos("Pending")}
          _hover={{ transform: "scale(1.05)" }}
          transition="all 0.2s"
        >
          Pending: {pendingCount}
        </Badge>
      </HStack>

      <Divider mb={4} />

      {/* Task List */}
      <Box maxH="calc(100vh - 300px)" overflowY="auto">
        <VStack spacing={3} align="stretch">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Box
                key={index}
                bg="gray.50"
                p={4}
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <Skeleton height="16px" width="60%" />
                  <Skeleton height="20px" width="80px" />
                </Flex>
                <SkeletonText noOfLines={2} spacing={2} />
              </Box>
            ))
          ) : todos?.length === 0 ? (
            <VStack spacing={4} py={12}>
              <CheckSquare size={64} color="#CBD5E0" />
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.700">
                  No tasks yet
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Create your first task to get started
                </Text>
              </VStack>
            </VStack>
          ) : (
            <Accordion allowToggle>
              {todoShow?.map((todo: any) => {
                const assigneeId = getTodoAssigneeId(todo);
                const assigneeDisplay = getTodoAssigneeDisplay(todo, users);
                const isUnauthorized =
                  user?.UserType === 3 &&
                  normalizeId(user?._id) !== getTodoRefId(todo?.createdBy) &&
                  normalizeId(user?._id) !== assigneeId;

                // Determine badge display logic
                const showOverdueBadge =
                  todo.status === "Pending" && isOverdue(todo.dueDate);
                const showWithinTimeBadge =
                  todo.status === "Completed" &&
                  isCompletedWithinTime(todo.dueDate, todo.updatedAt);

                return (
                  <AccordionItem
                    key={todo._id}
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="lg"
                    mb={3}
                    overflow="hidden"
                    position="relative"
                  >
                    {/* Badge on top-left corner */}
                    {(showOverdueBadge || showWithinTimeBadge) && (
                      <Box
                        position="absolute"
                        top="0"
                        left="0"
                        zIndex="10"
                        bg={showOverdueBadge ? "red.500" : "green.500"}
                        color="white"
                        px={3}
                        py={1}
                        borderBottomRightRadius="lg"
                        fontSize="xs"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        boxShadow="md"
                      >
                        {showOverdueBadge ? (
                          <>
                            <AlertCircle size={12} />
                            <Text>Run Out Of Time</Text>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={12} />
                            <Text>Within Time</Text>
                          </>
                        )}
                      </Box>
                    )}

                    <h2>
                      <AccordionButton
                        _expanded={{ bg: "blue.50" }}
                        px={4}
                        py={3}
                        pt={showOverdueBadge || showWithinTimeBadge ? 8 : 3}
                        _hover={{ bg: "gray.50" }}
                      >
                        <HStack flex="1" spacing={3} align="center">
                          {/* Task Info */}
                          <VStack align="start" spacing={1} flex="1">
                            <Text
                              fontSize="sm"
                              fontWeight="600"
                              color={
                                todo.status === "Completed"
                                  ? "gray.500"
                                  : "gray.800"
                              }
                              textDecoration={
                                todo.status === "Completed"
                                  ? "line-through"
                                  : "none"
                              }
                              noOfLines={1}
                            >
                              {todo.note}
                            </Text>
                            <Badge
                              colorScheme={
                                todo.status === "Completed" ? "green" : "orange"
                              }
                              fontSize="xs"
                            >
                              {todo.status}
                            </Badge>
                          </VStack>

                          {/* Action Buttons */}
                          <HStack spacing={2}>
                            {todo?.status !== "Completed" && (
                              <Tooltip
                                label={
                                  isUnauthorized
                                    ? "Not Authorized"
                                    : "Mark as completed"
                                }
                                hasArrow
                              >
                                <Box
                                  cursor={
                                    isUnauthorized ? "not-allowed" : "pointer"
                                  }
                                  pointerEvents={
                                    isUnauthorized ? "none" : "auto"
                                  }
                                  opacity={isUnauthorized ? 0.5 : 1}
                                >
                                  <UpdateTodoWithComment
                                    id={todo?._id}
                                    handleUpdate={handleMarkComplete}
                                  />
                                </Box>
                              </Tooltip>
                            )}

                            {user?.UserType !== 3 ||
                            normalizeId(user?._id) ===
                              getTodoRefId(todo?.createdBy) ? (
                              <Tooltip label="Delete" hasArrow>
                                <IconButton
                                  aria-label="Delete task"
                                  icon={<Trash2 size={16} />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(todo._id);
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip label="Not Authorized" hasArrow>
                                <Box
                                  cursor="not-allowed"
                                  pointerEvents="none"
                                  opacity={0.5}
                                >
                                  <IconButton
                                    aria-label="Delete task"
                                    icon={<Trash2 size={16} />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                  />
                                </Box>
                              </Tooltip>
                            )}
                          </HStack>
                        </HStack>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>

                    <AccordionPanel pb={4} bg="gray.50">
                      <VStack align="stretch" spacing={3}>
                        <HStack spacing={2} fontSize="xs" color="gray.600" align="start">
                          <Box pt={0.5} flexShrink={0}>
                            <User size={14} />
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">
                              Assigned to:{" "}
                              {!assigneeDisplay?.name && !assigneeDisplay?.email
                                ? "Unknown"
                                : assigneeDisplay?.name || "—"}
                            </Text>
                            {assigneeDisplay?.email ? (
                              <Text color="gray.500" fontSize="11px">
                                {assigneeDisplay.email}
                              </Text>
                            ) : null}
                          </VStack>
                        </HStack>

                        {todo?.status === "Completed" && (
                          <Box
                            bg="green.50"
                            p={3}
                            borderRadius="lg"
                            border="1px"
                            borderColor="green.200"
                          >
                            <HStack spacing={2} mb={2}>
                              <CheckCircle2 size={16} color="#16A34A" />
                              <Text
                                fontSize="xs"
                                fontWeight="bold"
                                color="green.700"
                              >
                                Completed
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color="gray.700" mb={1}>
                              {format(
                                new Date(todo?.updatedAt),
                                "dd/MM/yyyy HH:mm"
                              )}
                            </Text>
                            {todo?.completedComment && (
                              <Text fontSize="xs" color="gray.600" mt={2}>
                                Comment: {todo?.completedComment}
                              </Text>
                            )}
                          </Box>
                        )}

                        <Divider />

                        <VStack align="stretch" spacing={2} fontSize="xs">
                          <HStack spacing={2} color="gray.600" align="start">
                            <Box pt={0.5} flexShrink={0}>
                              <User size={14} />
                            </Box>
                            <VStack align="start" spacing={0}>
                              <Text>
                                Created by {todo?.createrName || "—"} at{" "}
                                {format(
                                  new Date(todo?.createdAt),
                                  "dd/MM/yyyy HH:mm"
                                )}
                              </Text>
                              {todo?.creatorEmail ? (
                                <Text color="gray.500" fontSize="11px">
                                  {todo.creatorEmail}
                                </Text>
                              ) : null}
                            </VStack>
                          </HStack>
                          <HStack spacing={2}>
                            <Calendar size={14} />
                            <Text
                              color={
                                new Date(todo?.dueDate) < new Date() &&
                                todo.status === "Pending"
                                  ? "red.500"
                                  : "gray.700"
                              }
                              fontWeight="medium"
                            >
                              {isValid(new Date(todo?.dueDate))
                                ? `Due: ${format(
                                    new Date(todo.dueDate),
                                    "dd/MM/yyyy HH:mm"
                                  )}`
                                : "Invalid date"}
                            </Text>
                          </HStack>
                        </VStack>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </VStack>
      </Box>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTodoToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={getTodoToDeleteName()}
        itemType="task"
        isDeleting={isDeleting}
        warningMessage="This action will remove the task. It can be restored by an administrator if needed."
      />
    </Box>
  );
};

export default TodoDemo;
